import React, {useMemo} from "react";
import OrgPortalSectionTitle from "./OrgPortalSectionTitle";
import {SportIcon} from "../../common/SportIcon";
import {Divider} from "@mui/material";
import useTranslation from "../../../i18n/useTranslation";
import {useNavigate} from "react-router-dom";

export default function OrgPortalDivisionsSection({divisions, theme}) {
    const {
        primaryColor,
        secondaryColor,
        surfaceColor,
        cardBorder,
        textOnSurface,
        textOnSecondary,
        accentColor,
    } = theme;
    const {t} = useTranslation();
    const navigate = useNavigate();

    const cardStyles = useMemo(() => {
        const baseText = textOnSurface || "#14151a";

        return {
            grid: {
                display: "grid",
                gap: 24, // responsive columns handled by CSS class
            },
            // 🔹 Generic card: surface + card border
            card: {
                borderRadius: 16,
                overflow: "hidden",
                border: `1px solid ${cardBorder || "rgba(0,0,0,0.08)"}`,
                boxShadow: "0 14px 34px rgba(0,0,0,0.10)",
                background: surfaceColor || "#fff",
                transition:
                    "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
                display: "flex",
                flexDirection: "column",
            },
            // 🔹 Card header: uses secondaryColor + textOnSecondary
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 12px",
                background: secondaryColor,
                color: textOnSecondary,
                borderBottom: `1px solid ${cardBorder || "rgba(0,0,0,0.06)"}`,
            },
            headerLeft: {display: "flex", alignItems: "center", gap: 8},
            // 🔹 Sport chip: pill on top of header, own surface
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.02em",
                background: surfaceColor || "rgba(255,255,255,0.95)",
                border: "1px solid rgba(0,0,0,0.10)",
                color: baseText,
            },
            // Status chips keep semantic success / neutral colors for clarity
            statusChipOpen: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                background: "rgba(40,167,69,0.10)",
                color: "rgba(25,135,84,1)",
                border: "1px solid rgba(25,135,84,0.25)",
            },
            statusChipClosed: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 900,
                background: "rgba(108,117,125,0.10)",
                color: "rgba(108,117,125,1)",
                border: "1px solid rgba(108,117,125,0.25)",
            },
            // 🔹 Card body: textOnSurface
            body: {padding: 12},
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: textOnSurface || baseText,
                lineHeight: 1.15,
            },
            sub: {
                marginTop: 6,
                fontSize: 12.5,
                fontWeight: 800,
                color: `${baseText}8A`,
            },
            metaGrid: {
                marginTop: 10,
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 8,
            },
            metaRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 10px",
                borderRadius: 12,
                // keep it aligned with theme (slight overlay over surface)
                background: `${primaryColor}08`,
                border: `1px solid ${cardBorder || "rgba(0,0,0,0.06)"}`,
            },
            metaLabel: {
                fontSize: 12,
                fontWeight: 900,
                color: `${baseText}99`,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            metaValue: {
                fontSize: 12.5,
                fontWeight: 900,
                color: baseText,
            },
            // 🔹 Footer stats row: light surface band
            footer: {
                display: "flex",
                alignItems: "stretch",
                justifyContent: "space-between",
                gap: 8,
                padding: "8px 10px 10px",
                borderTop: `1px solid ${cardBorder || "rgba(0,0,0,0.06)"}`,
                background: "rgba(0,0,0,0.015)",
            },
            // 🔹 Footer pill: same as hero counters
            footerItem: {
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 10px",
                borderRadius: 999,
                background: surfaceColor,
                border: `1px solid ${cardBorder || "rgba(0,0,0,0.06)"}`,
                minWidth: 0,
            },
            footerLeft: {
                display: "flex",
                alignItems: "center",
                gap: 6,
                minWidth: 0,
            },
            footerIcon: {
                fontSize: 13,
                opacity: 0.85,
            },
            footerLabel: {
                fontSize: 10,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: `${baseText}99`,
                whiteSpace: "nowrap",
            },
            // 🔹 Numbers = accentColor
            footerValue: {
                fontSize: 14,
                fontWeight: 900,
                color: accentColor || baseText,
                marginLeft: 6,
                flexShrink: 0,
            },
        };
    }, [
        surfaceColor,
        cardBorder,
        textOnSurface,
        textOnSecondary,
        accentColor,
        primaryColor,
        secondaryColor
    ]);

    const goToDivision = (division) => {
        if (!division?.slug) return;
        const seasonSlug = division.activeSeason?.slug;
        if (!seasonSlug) return; // or handle no season
        navigate(`/division/${division.slug}/${seasonSlug}`);
    };

    return (
        <section style={{marginBottom: 50}}>
            <OrgPortalSectionTitle title={t('common.divisions')} theme={theme}/>

            {/* 🔹 Section divider uses primaryColor as a strong accent */}
            <Divider style={{marginBottom: 20}} color={primaryColor}/>

            {divisions.length === 0 ? (
                <div
                    style={{
                        backgroundColor: surfaceColor,
                        borderRadius: 12,
                        padding: 20,
                        border: `1px solid ${cardBorder}`,
                        color: textOnSurface,
                        fontSize: 14,
                        opacity: 0.8,
                    }}
                >
                    {t('home.divisions.noItems')}
                </div>
            ) : (
                <div
                    style={cardStyles.grid}
                    className="org-portal-division-grid"
                >
                    {divisions.map((division) => {
                        const isOpen = !!division.isOpen;
                        const hasSeason = !!division.activeSeason;

                        const seasonsCount =
                            division.openSeasons?.length != null
                                ? division.openSeasons.length
                                : division.seasonsCount ?? 0;
                        const teamsCount = division.teamsCount ?? 0;
                        const playersCount = division.playersCount ?? 0;

                        return (
                            <article
                                key={division.id || division.slug || division.name}
                                style={{
                                    ...cardStyles.card,
                                    cursor: "pointer", // UX hint
                                }}
                                className="org-portal-division-card"
                                onClick={() => goToDivision(division)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        goToDivision(division);
                                    }
                                }}
                            >
                                {/* Header: sport + status */}
                                <div style={cardStyles.header}>
                                    <div style={cardStyles.headerLeft}>
                                        <span style={cardStyles.chip} title="Sport">
                                            <SportIcon
                                                sportKey={division?.sport?.key || "soccer"}
                                                className="text-lg"
                                            />
                                            {division?.sport?.name || "Sport"}
                                        </span>
                                    </div>

                                    <span
                                        style={
                                            isOpen
                                                ? cardStyles.statusChipOpen
                                                : cardStyles.statusChipClosed
                                        }
                                    >
                                        <i
                                            className={
                                                isOpen
                                                    ? "fas fa-check-circle"
                                                    : "fas fa-lock"
                                            }
                                        />
                                        {isOpen ? t('home.divisions.open') : t('home.divisions.closed')}
                                    </span>
                                </div>

                                {/* Body */}
                                <div style={cardStyles.body}>
                                    <h3 style={cardStyles.title}>{division.name}</h3>
                                    <div style={cardStyles.sub}>
                                        {division?.organization?.name
                                            ? `Organization: ${division.organization.name}`
                                            : " "}
                                    </div>

                                    <div style={cardStyles.metaGrid}>
                                        {hasSeason ? (
                                            <>
                                                <span style={cardStyles.metaLabel}>
                                                    {division.activeSeason?.name}
                                                </span>
                                                <div style={cardStyles.metaRow}>
                                                    <span style={cardStyles.metaLabel}>
                                                        {t('home.divisions.seasonStart')}
                                                    </span>
                                                    <span style={cardStyles.metaValue}>
                                                        {division.activeSeason.startDate}
                                                    </span>
                                                </div>
                                                <div style={cardStyles.metaRow}>
                                                    <span style={cardStyles.metaLabel}>
                                                        {t('home.divisions.seasonEnd')}
                                                    </span>
                                                    <span style={cardStyles.metaValue}>
                                                        {division.activeSeason.endDate}
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <div style={cardStyles.metaRow}>
                                                <span style={cardStyles.metaLabel}>{t('common.season')}</span>
                                                <span
                                                    style={cardStyles.metaValue}
                                                    title="No active season configured"
                                                >
                                                    -
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Footer stats */}
                                <div style={cardStyles.footer}>
                                    <div style={cardStyles.footerItem}>
                                        <div style={cardStyles.footerLeft}>
                                            <i
                                                className="fas fa-flag"
                                                style={cardStyles.footerIcon}
                                            />
                                            <span style={cardStyles.footerLabel}>{t('common.seasons')}</span>
                                        </div>
                                        <span style={cardStyles.footerValue}>
                                            {seasonsCount}
                                        </span>
                                    </div>

                                    <div style={cardStyles.footerItem}>
                                        <div style={cardStyles.footerLeft}>
                                            <i
                                                className="fas fa-users"
                                                style={cardStyles.footerIcon}
                                            />
                                            <span style={cardStyles.footerLabel}>{t('common.teams')}</span>
                                        </div>
                                        <span style={cardStyles.footerValue}>
                                            {teamsCount}
                                        </span>
                                    </div>

                                    <div style={cardStyles.footerItem}>
                                        <div style={cardStyles.footerLeft}>
                                            <i
                                                className="fas fa-user"
                                                style={cardStyles.footerIcon}
                                            />
                                            <span style={cardStyles.footerLabel}>{t('common.players')}</span>
                                        </div>
                                        <span style={cardStyles.footerValue}>
                                            {playersCount}
                                        </span>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            <style>{`
              .org-portal-division-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 18px 42px rgba(0,0,0,0.14) !important;
                border-color: ${primaryColor} !important; /* 🔹 Hover border uses primary */
              }

              /* Responsive grid:
                 - <768px: 1 per row
                 - 768–1199px: 2 per row
                 - ≥1200px: 3 per row
              */
              .org-portal-division-grid {
                grid-template-columns: 1fr;
              }

              @media (min-width: 768px) {
                .org-portal-division-grid {
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                }
              }

              @media (min-width: 1200px) {
                .org-portal-division-grid {
                  grid-template-columns: repeat(3, minmax(0, 1fr));
                }
              }
            `}</style>
        </section>
    );
}